import { useEffect, useRef, useState } from "react";
import { F } from "../theme/tokens";

/**
 * Schede informative mostrate durante l'elaborazione.
 *
 * L'attesa misurata su un referto scansionato di 6 pagine e' di circa 26
 * secondi, ma con piu' file si somma. Si alternano ogni 16 secondi: a otto il
 * testo piu' lungo non si faceva in tempo a leggerlo. L'ordine e' casuale a
 * ogni elaborazione, cosi' chi usa il servizio piu' volte non rivede sempre
 * le stesse schede.
 *
 * NOTA SUI CONTENUTI. Le schede che descrivono il funzionamento di Obscura
 * sono ancorate al codice, non allo slogan:
 *   - "entro un'ora" e' JOB_TTL_SECONDS (default 3600) in redis_store.py;
 *   - "non viene scritto su disco ne' nei log" e' cio' che fa
 *     italian_ner.py con il report dei valori originali;
 *   - "58 categorie" sono quelle di categories.py.
 * Se uno di quei comportamenti cambia, il testo diventa falso e va cambiato
 * con lui. Le schede che toccano GDPR e AI Act restano affermazioni
 * normative su un prodotto che vende conformita': vanno riviste prima di
 * ogni pubblicazione, e non devono mai promettere un esito legale.
 */
export const PROCESSING_CARDS = [
  {
    titolo: "Il nome da solo non basta",
    testo:
      "Una persona resta riconoscibile anche attraverso indirizzi, date, ruoli e numeri identificativi presenti nel documento. Per questo, OBSCURA non cerca soltanto nomi e cognomi: analizza 58 categorie di dati pensate per i documenti italiani, così puoi individuare più facilmente le informazioni da anonimizzare e controllare il risultato nell'editor.",
  },
  {
    titolo: "Cosa succede al tuo documento",
    testo:
      "Il file che carichi resta sui nostri server europei per il solo tempo dell'elaborazione e viene cancellato appena il processo termina. Il file redatto vive in una memoria temporanea e viene distrutto entro un'ora. I dati sensibili individuati da OBSCURA vengono mantenuti solo nella memoria temporanea necessaria all'elaborazione; non vengono salvati su disco né registrati nei log e, al termine del processo, non restano memorizzati sul server.",
  },
  {
    titolo: "Non tutte le redazioni sono uguali",
    testo:
      "OBSCURA distingue le redazioni più certe da quelle che meritano un secondo sguardo. Queste ultime restano oscurate, ma nell'editor le trovi con un bordo ambra e la loro categoria. In questo modo, puoi concentrare la revisione sui passaggi che richiedono più attenzione, senza dover controllare l'intero documento.",
  },
  {
    titolo: "Posso scegliere le informazioni da anonimizzare?",
    testo:
      "Sì. Prima dell'elaborazione, puoi scegliere quali categorie di dati anonimizzare. Un contratto, una cartella clinica e un documento HR contengono informazioni molto diverse: selezionare le categorie pertinenti permette di adattare OBSCURA al documento che stai trattando. Il risultato è una redazione più focalizzata, nella quale sai già quali tipi di informazioni il sistema ha cercato e classificato.",
  },
  {
    titolo: "Anonimizzazione vs Pseudonimizzazione",
    testo:
      "Il GDPR incoraggia a ridurre l'uso e l'esposizione dei dati personali. Pseudonimizzare significa sostituire gli identificativi mantenendo la possibilità di ricondurli alla persona; anonimizzare significa invece rendere la persona non più identificabile nel contesto considerato. OBSCURA anonimizza le informazioni senza conservare una mappa persistente tra dato originale e redazione, in linea con il GDPR.",
  },
  {
    titolo: "Chi decide cosa resta anonimizzato?",
    testo:
      "Tu. OBSCURA individua e anonimizza in automatico, ma l'ultima parola resta a chi revisiona: nell'editor integrato puoi rivedere e modificare le aree da anonimizzare, aggiungendo, rimuovendo o regolando i riquadri suggeriti dall'algoritmo prima del download. Il documento finale viene esportato solo dopo la tua revisione.",
  },
  {
    titolo: "Funziona anche con i PDF scansionati?",
    testo:
      "Sì. Se il documento è un'immagine o una scansione, OBSCURA lo legge prima con il riconoscimento ottico dei caratteri e lavora poi sul testo estratto. La qualità della scansione però conta: una pagina dritta, a fuoco e ad almeno 300 DPI produce meno errori di lettura, e meno errori di lettura significano meno aree incerte da rivedere alla fine.",
  },
  {
    titolo: "Pensato per i documenti italiani",
    testo:
      "Codice fiscale, partita IVA, numeri di sentenza, regime patrimoniale: sono forme tipiche della documentazione italiana, che un modello generalista addestrato altrove fatica a riconoscere. OBSCURA lavora su 58 categorie dedicate, costruite attorno a documenti legali, sanitari, amministrativi e HR del contesto italiano.",
  },
  {
    titolo: "E se nel documento c'è un volto?",
    testo:
      "Non sono solo nomi, codici o indirizzi a rendere riconoscibile una persona: anche il volto in una foto o in una scansione è considerato un dato sensibile. Per questo, OBSCURA permette di individuare i volti presenti nei documenti e sfocarli, affiancando la protezione delle immagini all'anonimizzazione dei dati testuali.",
  },
  {
    titolo: "Oltre le singole parole",
    testo:
      "La stessa parola può essere un dato personale in una riga e un termine comune in quella dopo. Per distinguere i due casi, OBSCURA impiega algoritmi proprietari, addestrati su documentazione italiana, che valutano ogni occorrenza nel contesto in cui compare. Invece di applicare delle regole statiche di anonimizzazione, OBSCURA capisce il contesto e categorizza le parole di conseguenza.",
  },
  {
    titolo: "Anonimizza prima di condividere",
    testo:
      "Quando un documento esce dal suo contesto originario — inviato a un consulente, girato internamente, usato da terzi — conviene limitare le informazioni personali che porta con sé. OBSCURA individua e oscura le categorie che hai scelto lasciando il documento leggibile e utilizzabile. È un approccio coerente con il principio GDPR di minimizzazione dei dati.",
  },
  {
    titolo: "Come controlli la bontà del risultato?",
    testo:
      "OBSCURA non si limita a consegnarti un file anonimizzato: apre un editor dove vedi ogni redazione, riconosci la categoria che l'ha generata e trovi evidenziate le aree più incerte, che hanno bisogno dell'intervento umano. In questo modo, il controllo sul risultato finale passa sempre dalle mani del revisore umano.",
  },
];

const S = {
  bianco: "#ffffff",
  bordo: "#def0f9",
  blu: "#009CDD",
  titolo: "#182E4F",
  corpo: "#2F4361",
};

const INTERVALLO_MS = 16000;
const TICK_MS = 80;
// Altezza fissa: le schede hanno testi di lunghezza diversa e un contenitore
// che si adatta le farebbe saltare a ogni cambio, sotto gli occhi di chi
// legge. I valori sono misurati sulla scheda piu' lunga alle larghezze reali
// (524px desktop, 358px mobile): servono 234 e 274, il resto e' margine per
// un ripiego di font diverso dal previsto. Da rimisurare se i testi cambiano:
// una scheda piu' lunga di cosi' verrebbe tagliata in silenzio.
const ALTEZZA = { desktop: 250, mobile: 292 };

/** Mescola una copia dell'array (Fisher-Yates). */
function mescola(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Freccia({ verso }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {verso === "sinistra" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

function bottoneFreccia(isMobile) {
  return {
    width: isMobile ? 32 : 36,
    height: isMobile ? 32 : 36,
    flexShrink: 0,
    borderRadius: "50%",
    border: `1px solid ${S.bordo}`,
    background: S.bianco,
    color: S.blu,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

/**
 * @param {{ isMobile?: boolean, schede?: typeof PROCESSING_CARDS }} props
 */
export default function ProcessingCards({ isMobile = false, schede = PROCESSING_CARDS }) {
  // Mescolate una volta sola, al montaggio: il componente monta quando parte
  // l'elaborazione, quindi ogni lavoro ha un ordine suo, ma l'ordine non
  // cambia sotto gli occhi di chi sta leggendo.
  const [ordine] = useState(() => mescola(schede));
  const [i, setI] = useState(0);
  // L'avanzamento automatico si ferma per sempre al primo tocco: continuare a
  // spostare la scheda mentre l'utente legge e' il modo piu' rapido di
  // rendere il carosello fastidioso.
  const [automatico, setAutomatico] = useState(true);
  const [trascorso, setTrascorso] = useState(0);
  const inizio = useRef(0);

  const totale = ordine.length;

  useEffect(() => {
    if (!automatico || totale < 2) return undefined;
    // Il conto alla rovescia sta in JavaScript e non in una animazione CSS
    // apposta: con "prefers-reduced-motion: reduce" le animazioni vengono
    // azzerate, e il pallino resterebbe fermo proprio mentre il tempo scorre.
    inizio.current = Date.now();
    setTrascorso(0);
    const t = setInterval(() => {
      const q = (Date.now() - inizio.current) / INTERVALLO_MS;
      if (q >= 1) {
        // Fermato subito, senza aspettare la pulizia dell'effetto: fra il
        // setI e il nuovo render passa qualche decina di millisecondi, e un
        // altro scatto dell'intervallo salterebbe una scheda.
        clearInterval(t);
        setI((v) => (v + 1) % totale);
      } else {
        setTrascorso(q);
      }
    }, TICK_MS);
    return () => clearInterval(t);
  }, [automatico, totale, i]);

  const manuale = (n) => {
    setAutomatico(false);
    setTrascorso(0);
    setI(((n % totale) + totale) % totale);
  };

  const scheda = ordine[i];
  if (!scheda) return null;

  return (
    <section aria-label="Informazioni utili durante l'attesa" style={{ width: "100%", maxWidth: 620, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Su telefono le frecce ai lati toglierebbero 80px di larghezza alla
            scheda, che dovrebbe crescere di 61px in altezza per contenere lo
            stesso testo (misurato). Li' vanno sotto, con i pallini: la scheda
            prende tutta la riga e il pollice le raggiunge meglio. */}
        {!isMobile && (
          <button type="button" onClick={() => manuale(i - 1)} style={bottoneFreccia(isMobile)} aria-label="Scheda precedente">
            <Freccia verso="sinistra" />
          </button>
        )}

        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: isMobile ? ALTEZZA.mobile : ALTEZZA.desktop,
            boxSizing: "border-box",
            background: S.bianco,
            border: `1px solid ${S.bordo}`,
            borderRadius: 15,
            padding: isMobile ? "18px 18px 20px" : "22px 26px 24px",
            boxShadow: "0 2px 12px rgba(24,46,79,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            overflow: "hidden",
          }}
        >
          <h3 style={{ margin: 0, fontFamily: F.display, fontSize: isMobile ? 17 : 19, fontWeight: 600, color: S.titolo, lineHeight: 1.25, flexShrink: 0 }}>
            {scheda.titolo}
          </h3>
          <p style={{ margin: 0, fontFamily: F.body, fontSize: isMobile ? 13 : 14, color: S.corpo, lineHeight: 1.55 }}>
            {scheda.testo}
          </p>
        </div>

        {!isMobile && (
          <button type="button" onClick={() => manuale(i + 1)} style={bottoneFreccia(isMobile)} aria-label="Scheda successiva">
            <Freccia verso="destra" />
          </button>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: isMobile ? 12 : 8, marginTop: 14 }}>
        {isMobile && (
          <button type="button" onClick={() => manuale(i - 1)} style={bottoneFreccia(isMobile)} aria-label="Scheda precedente">
            <Freccia verso="sinistra" />
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, flexWrap: "wrap", justifyContent: "center" }}>
        {ordine.map((s, n) => {
          const attivo = n === i;
          return (
            <button
              key={s.titolo}
              type="button"
              onClick={() => manuale(n)}
              aria-label={`Vai alla scheda ${n + 1} di ${totale}: ${s.titolo}`}
              aria-current={attivo ? "true" : undefined}
              style={{
                // Il pallino attivo e' largo: a 26px il riempimento avanzava
                // di 3px al secondo, cioe' meno di un pixel per aggiornamento,
                // e a occhio sembrava fermo. A 44px il movimento si vede.
                width: attivo ? 44 : 8,
                height: 8,
                padding: 0,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                overflow: "hidden",
                background: S.bordo,
                transition: "width .3s ease",
              }}
            >
              {attivo && (
                // Il pallino attivo si riempie col passare del tempo: dice
                // quanto manca al cambio, e mostra a colpo d'occhio che
                // l'avanzamento si e' fermato dopo un tocco (barra piena,
                // ferma).
                <span
                  aria-hidden="true"
                  style={{
                    display: "block",
                    height: "100%",
                    width: `${((automatico ? trascorso : 1) * 100).toFixed(1)}%`,
                    background: S.blu,
                    borderRadius: 999,
                    // Il conteggio avanza a scatti di TICK_MS; la transizione
                    // lineare della stessa durata interpola i fotogrammi in
                    // mezzo, e lo scorrimento si vede continuo invece che a
                    // gradini. E' una transition, non una animation: resta
                    // viva anche con le animazioni ridotte.
                    transition: `width ${TICK_MS}ms linear`,
                  }}
                />
              )}
            </button>
          );
        })}
        </div>
        {isMobile && (
          <button type="button" onClick={() => manuale(i + 1)} style={bottoneFreccia(isMobile)} aria-label="Scheda successiva">
            <Freccia verso="destra" />
          </button>
        )}
      </div>
    </section>
  );
}
